import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import VatVerificationModal from './VatVerificationModal';
import VatPaymentVerificationModal from './VatPaymentVerificationModal';
import moment from 'moment';

// Import new components
import VatModernHeader from './components/VatModernHeader';
import VatProgressTracker from './components/VatProgressTracker';
import VatStepContent from './components/VatStepContent';
import VatStepNavigation from './components/VatStepNavigation';

import {
    CheckCircle2,
    Info,
    Eye,
    Send,
    CreditCard,
    BookOpen
} from 'lucide-react';

//import { toast } from 'react-toastify';

import { toast } from 'sonner';

// Step definitions (matching current implementation)
const StepEnum = {
    INSTRUCTIONS: 'instructions',
    REVIEW: 'review',
    SUBMISSION: 'submission',
    PAYMENT: 'payment',
    RECORD: 'record'
};

const stepTitles = {
    [StepEnum.INSTRUCTIONS]: "Instruktioner",
    [StepEnum.REVIEW]: "Granska Momsrapporten",
    [StepEnum.SUBMISSION]: "Skicka momsrapport",
    [StepEnum.PAYMENT]: "Betala momsen",
    [StepEnum.RECORD]: "Bokför dragning"
};

const stepOrder = [
    StepEnum.INSTRUCTIONS,
    StepEnum.REVIEW,
    StepEnum.SUBMISSION,
    StepEnum.PAYMENT,
    StepEnum.RECORD
];

const stepIcons = {
    [StepEnum.INSTRUCTIONS]: Info,
    [StepEnum.REVIEW]: Eye,
    [StepEnum.SUBMISSION]: Send,
    [StepEnum.PAYMENT]: CreditCard,
    [StepEnum.RECORD]: BookOpen
};

const accountNumbers = [
    2611, 2621, 2631,
    2612, 2622, 2632,
    2613, 2623, 2633,
    2616, 2626, 2636,
    2614, 2624, 2634,
    2615, 2625, 2635,
];

function VatPeriodStepper() {
    const { companyId, vatPeriodId, fiscalYearId } = useParams();

    // State management (matching current implementation)
    const [vatPeriod, setVatPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [undoingClose, setUndoingClose] = useState(false);
    const [currentStep, setCurrentStep] = useState(StepEnum.INSTRUCTIONS);
    const [showModal, setShowModal] = useState(false);
    const [showVatModal, setShowVatModal] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [vatData, setVatData] = useState(null);
    const [totalVat, setTotalVat] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [totalDebit, setTotalDebit] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [operationInProgress, setOperationInProgress] = useState(false);

    // Verification tracking state
    const [reviewVerification, setReviewVerification] = useState(null);
    const [recordVerification, setRecordVerification] = useState(null);
    const [paymentMadeDate, setPaymentMadeDate] = useState(null);
    const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
    const [verificationModalType, setVerificationModalType] = useState(null); // 'review' or 'record'

    // Step completion tracking state
    const [completionStatus, setCompletionStatus] = useState({
        instructions_completed: false,
        review_completed: false,
        submission_completed: false,
        payment_completed: false,
        record_completed: false
    });

    // Data fetching (identical to current implementation)
    useEffect(() => {
        const fetchVatPeriodDetails = async () => {
            try {
                const response = await axios.get(getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`), { withCredentials: true });
                if (response.data.success) {
                    setVatPeriod(response.data.period);
                    setCurrentStep(response.data.period.current_step);
                } else {
                    setError('Failed to load VAT period details');
                }
            } catch (err) {
                setError('Error fetching VAT period details');
            } finally {
                setLoading(false);
            }
        };

        fetchVatPeriodDetails();
    }, [companyId, vatPeriodId]);

    useEffect(() => {
        if (vatPeriod) {
            fetchVATCalculations();
        }
    }, [vatPeriod]);

    // Check for existing verifications when step changes or on mount
    useEffect(() => {
        const checkExistingVerifications = async () => {
            if (!vatPeriod || !vatPeriodId) return;

            try {
                // Get completion status for all steps
                const statusResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/completion-status`),
                    { withCredentials: true }
                );

                if (statusResponse.data.success) {
                    const status = statusResponse.data.status;
                    setReviewVerification(status.review_verification);
                    setRecordVerification(status.record_verification);
                    setPaymentMadeDate(status.payment_date);

                    // Set completion status from database
                    setCompletionStatus({
                        instructions_completed: status.instructions_completed || false,
                        review_completed: status.review_completed || false,
                        submission_completed: status.submission_completed || false,
                        payment_completed: status.payment_completed || false,
                        record_completed: status.record_completed || false
                    });
                }
            } catch (error) {
                console.error('Error checking existing verifications:', error);
            }
        };

        checkExistingVerifications();
    }, [vatPeriod?.current_step, vatPeriodId, companyId]);

    const fetchVATCalculations = async () => {
        try {
            const getVATCalculationsApiUrl = getApiUrl(`${companyId}/reporting/vat-calculation`);
            const response = await axios.get(getVATCalculationsApiUrl, {
                withCredentials: true,
                params: {
                    start_date: vatPeriod.start_date,
                    end_date: vatPeriod.end_date,
                },
            });

            if (response.data.success) {
                setVatData(response.data.vatData);
                setTotalVat(response.data.totalVat);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching VAT calculations:', error);
        }
    };

    // Business logic methods (identical to current implementation)
    const handleStepComplete = async (nextStep) => {
        setOperationInProgress(true);
        try {
            const validSteps = ['instructions', 'review', 'submission', 'payment', 'record'];

            if (!validSteps.includes(nextStep)) {
                throw new Error('Invalid step value');
            }

            // Special handling for submission step - generate report
            if (nextStep === StepEnum.SUBMISSION) {
                const vatReportData = {
                    orgNr: '556000-0175',
                    period: '202401',
                    vatData: vatData,
                    totalVat: totalVat
                };

                const generateReportResponse = await axios.post(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report`),
                    { vatReportData: vatReportData },
                    { withCredentials: true }
                );
                if (!generateReportResponse.data.success) {
                    throw new Error('Failed to generate VAT report');
                }
            }

            await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/update-step`),
                { current_step: nextStep },
                { withCredentials: true }
            );

            setCurrentStep(nextStep);

        } catch (err) {
            setError('Error updating current step');
            console.error('Error in handleStepComplete:', err.message || err);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleCompletePeriod = async () => {
        setOperationInProgress(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/close`),
                { fiscalYearId: vatPeriod.fiscal_year_id },
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'closed' }));
                // Refresh period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }
            } else {
                setError('Failed to close VAT period');
            }
        } catch (err) {
            setError('Error closing VAT period');
            console.error('Error in handleCompletePeriod:', err);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleVatPeriodClose = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/close`),
                { fiscalYearId: vatPeriod.fiscal_year_id },
                { withCredentials: true }
            );

            if (response.data.success) {
                // Update local state
                setVatPeriod(prevState => ({ ...prevState, state: 'closed', current_step: 'record' }));
                setCurrentStep(StepEnum.RECORD);

                // Show success message
                toast.success('Momsperioden har stängts', {
                    description: 'Perioden är nu markerad som avslutad'
                });
            } else {
                setError('Failed to close VAT period');
                toast.error('Kunde inte stänga momsperioden');
            }
        } catch (err) {
            setError('Error closing VAT period');
            toast.error('Ett fel uppstod vid stängning av momsperioden');
            console.error('Error closing VAT period:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUndoClose = async () => {
        setUndoingClose(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/undo-close`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'open' }));
                setCurrentStep(StepEnum.INSTRUCTIONS);
            } else {
                setError('Failed to reopen VAT period');
            }
        } catch (err) {
            setError('Error reopening VAT period');
        } finally {
            setUndoingClose(false);
        }
    };

    // Additional handler methods (from current implementation)
    const handleReviewVatPeriod = () => {
        const getVatPeriodVerificationSuggestion = getApiUrl(`${companyId}/accounting/vat-period/${vatPeriodId}/vat-verification`);
        axios.post(getVatPeriodVerificationSuggestion, {
            fiscalYearId: fiscalYearId || vatPeriod.fiscal_year_id,
            startDate: vatPeriod.start_date,
            endDate: vatPeriod.end_date,
            accountNumbers: accountNumbers
        }, {
            withCredentials: true
        })
            .then((response) => {
                setTransactions(response.data.suggestions);
                calculateTotals(response.data.suggestions);
                setVerificationModalType('review');
                setShowVatModal(true);
            })
            .catch((error) => {
                console.error('Error fetching VAT period verification suggestion:', error);
            });
    };

    const calculateTotals = (transactions) => {
        const totalDebit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.debit || 0), 0);
        const totalCredit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.credit || 0), 0);
        setTotalDebit(totalDebit);
        setTotalCredit(totalCredit);
    };

    const handlePayment = () => {
        const generateBookkeepingSuggestion = async (templateId, parameters, userSelectedAccount) => {
            try {
                const suggestionApiUrl = getApiUrl(`${companyId}/templates/${templateId}/suggestion`);
                const response = await axios.post(suggestionApiUrl, { parameters, userSelectedAccount }, { withCredentials: true });

                response.data.suggestedTransactions.sort((a, b) => a.account_number - b.account_number);
                setTransactions(response.data.suggestedTransactions);

                const totalDebit = response.data.suggestedTransactions.reduce((acc, transaction) => acc + parseFloat(transaction.debit || 0), 0);
                const totalCredit = response.data.suggestedTransactions.reduce((acc, transaction) => acc + parseFloat(transaction.credit || 0), 0);
                setTotalDebit(totalDebit);
                setTotalCredit(totalCredit);
            } catch (error) {
                console.error('Error generating bookkeeping suggestion:', error);
            }
        };

        const paramsForVerification = {
            "date": "2024-09-12",
            "description": "test",
            "series": "V",
            "parameters": {
                "amount": totalVat,
            },
            "userDefinedAccounts": {}
        };

        const templateId = "355cbdc1-7a8e-4e29-9961-d764230faa46";
        generateBookkeepingSuggestion(templateId, paramsForVerification.parameters, paramsForVerification.userDefinedAccounts);
        setShowModal(true);
    };

    const handleConfirmVatVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = new Date().toISOString().split('T')[0];
            const description = `Momsredovisning för perioden: ${vatPeriod.start_date} - ${vatPeriod.end_date}`;
            //const series = 'A';
            const attachments = [];

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                //series,
                transactions,
                attachments,
                reference_data: {  // NEW: Link to VAT period
                    type: 'vat_reporting_period',
                    vat_period_id: vatPeriodId,
                    step: 'review_step'
                }
            }, { withCredentials: true });

            if (response.data.success) {
                // Store the created verification info
                setReviewVerification({
                    verification_id: response.data.verificationId,
                    series: response.data.series,
                    number: response.data.verificationNumber
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark review step as completed
                await markStepComplete('review');

                // Show success message
                //const swal = (await import('sweetalert')).default;
                //swal("Framgång", "Verifikat skapat och kopplat till momsperioden", "success");
                toast.success("Verifikat skapat och kopplat till momsperioden");
            } else {
                throw new Error('Failed to create verification');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmVatVerification:', error);
        } finally {
            setVerifying(false);
        }
    };

    const handleConfirmVerification = async (paymentDate) => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = paymentDate || moment(vatPeriod.end_date).format('YYYY-MM-DD');
            const description = `Momsbetalning ${moment(vatPeriod.start_date).format('MMMM YYYY')}`;
            const attachments = [];

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                transactions,
                attachments
            }, { withCredentials: true });

            if (response.data.success) {
                // Verification created successfully
                toast.success("Betalningsverifikat skapat");

                // Mark payment as made with the same date
                await handlePaymentMade(date);

                setShowModal(false);
                setTransactions([]);
            } else {
                setError('Kunde inte skapa verifikatet');
            }
        } catch (error) {
            setError('Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmVerification:', error);
            toast.error(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
        } finally {
            setVerifying(false);
        }
    };

    const handleDeleteVerification = async (verificationId, step) => {
        const swal = (await import('sweetalert')).default;
        const confirmDelete = await swal({
            title: "Ta bort verifikat?",
            text: "Är du säker på att du vill ta bort detta verifikat? Detta kan inte ångras.",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ta bort",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmDelete) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/verification/${verificationId}/${step}`),
                { withCredentials: true }
            );

            if (response.data.success) {
                // Clear the verification from state
                if (step === 'review') {
                    setReviewVerification(null);
                    // Unmark review step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/review`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, review_completed: false }));
                } else if (step === 'record') {
                    setRecordVerification(null);
                    // Unmark record step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/record`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, record_completed: false }));
                }

                //swal("Framgång", "Verifikat borttaget", "success");
                toast.success("Verifikat borttaget");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ta bort verifikat';
            //swal("Fel", message, "error");
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handlePaymentMade = async (paymentDate) => {
        setOperationInProgress(true);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { payment_date: paymentDate },
                { withCredentials: true }
            );

            if (response.data.success) {
                setPaymentMadeDate(paymentDate);
                setShowPaymentDateModal(false);

                // Mark payment step as completed
                await markStepComplete('payment');

                //const swal = (await import('sweetalert')).default;
                //swal("Framgång", "Betalning registrerad", "success");
                toast.success("Betalning registrerad");
            }
        } catch (error) {
            //const swal = (await import('sweetalert')).default;
            //swal("Fel", "Kunde inte registrera betalning", "error");
            toast.error("Kunde inte registrera betalning");
        } finally {
            setOperationInProgress(false);
        }
    };

    // Payment verification creation removed - users handle payment outside VAT flow
    // Future: Automated agents will create payment verifications automatically

    const handleClearPayment = async () => {
        const swal = (await import('sweetalert')).default;
        const confirmClear = await swal({
            title: "Ångra betalning?",
            text: "Är du säker på att du vill ta bort betalningsdatumet?",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ta bort",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmClear) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { withCredentials: true }
            );

            if (response.data.success) {
                setPaymentMadeDate(null);

                // Unmark payment step as completed
                await axios.delete(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/payment`),
                    { withCredentials: true }
                );
                setCompletionStatus(prev => ({ ...prev, payment_completed: false }));

                //swal("Framgång", "Betalning borttagen", "success");
                toast.success("Betalning borttagen");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ta bort betalning';
            //swal("Fel", message, "error");
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleCreateRecordVerification = async () => {
        setOperationInProgress(true);
        try {
            // Fetch account details to get proper account names
            const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts`);
            const accountsResponse = await axios.get(accountsApiUrl, { withCredentials: true });
            const accounts = accountsResponse.data.accounts || accountsResponse.data || [];

            // Find accounts 1630 and 2650
            const account1630 = accounts.find(acc => acc.account_number === '1630');
            const account2650 = accounts.find(acc => acc.account_number === '2650');

            // Prepare record verification transactions
            // When Skatteverket withdraws: Debit 2650 (VAT clearing), Credit 1630 (Tax account)
            const recordTransactions = [
                {
                    account_id: 2650,
                    account_number: '2650',
                    account_name: account2650?.account_name || 'Redovisningskonto för moms',
                    debit: Math.abs(totalVat),
                    credit: 0,
                    transaction_description: `Skatteverkets dragning moms ${moment(vatPeriod.start_date).format('MMMM YYYY')}`
                },
                {
                    account_id: 1630,
                    account_number: '1630',
                    account_name: account1630?.account_name || 'Avräkning för skatter och avgifter',
                    debit: 0,
                    credit: Math.abs(totalVat),
                    transaction_description: `Skatteverkets dragning moms ${moment(vatPeriod.start_date).format('MMMM YYYY')}`
                }
            ];

            // Calculate totals
            const debitTotal = Math.abs(totalVat);
            const creditTotal = Math.abs(totalVat);

            // Set transactions and totals, then open the VAT verification modal
            setTransactions(recordTransactions);
            setTotalDebit(debitTotal);
            setTotalCredit(creditTotal);
            setVerificationModalType('record');
            setShowVatModal(true);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Kunde inte hämta kontoinformation');
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleConfirmRecordVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const date = moment().format('YYYY-MM-DD');
            const description = `Dragning Skatteverket: ${moment(vatPeriod.start_date).format('MMMM YYYY')}`;

            const response = await axios.post(createVerificationApiUrl, {
                date,
                description,
                transactions,
                attachments: [],
                reference_data: {
                    type: 'vat_reporting_period',
                    vat_period_id: vatPeriodId,
                    step: 'record_step'
                }
            }, { withCredentials: true });

            if (response.data.success) {
                // Store the created verification info
                setRecordVerification({
                    verification_id: response.data.verificationId,
                    series: response.data.series,
                    number: response.data.verificationNumber
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark record step as completed (this also closes the period)
                await markStepComplete('record');

                // Reload VAT period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }

                toast.success("Verifikat för dragning skapat och momsperiod slutförd");
            } else {
                setError('Kunde inte skapa verifikatet');
            }
        } catch (error) {
            setError('Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmRecordVerification:', error);
            toast.error(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
        } finally {
            setVerifying(false);
        }
    };

    // Step completion marking
    const markStepComplete = async (step) => {
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/${step}`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setCompletionStatus(prev => ({
                    ...prev,
                    [`${step}_completed`]: true
                }));
                toast.success(`Steg "${step}" markerat som klart`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte markera steg som klart');
        }
    };

    // Step navigation helpers
    const isStepCompleted = (step) => {
        return stepOrder.indexOf(step) < stepOrder.indexOf(currentStep);
    };

    const isStepActive = (step) => {
        return step === currentStep;
    };

    const getCurrentStepIndex = () => {
        return stepOrder.indexOf(currentStep);
    };

    const canGoToNextStep = () => {
        const currentIndex = getCurrentStepIndex();

        // Basic check: can't go past last step
        if (currentIndex >= stepOrder.length - 1) {
            return false;
        }

        // Check if current step is completed using completion status
        const stepCompletionMap = {
            [StepEnum.INSTRUCTIONS]: completionStatus.instructions_completed,
            [StepEnum.REVIEW]: completionStatus.review_completed,
            [StepEnum.SUBMISSION]: completionStatus.submission_completed,
            [StepEnum.PAYMENT]: completionStatus.payment_completed,
            [StepEnum.RECORD]: completionStatus.record_completed
        };

        return stepCompletionMap[currentStep] || false;
    };

    const canGoToPreviousStep = () => {
        const currentIndex = getCurrentStepIndex();

        // Basic check: can't go before first step
        if (currentIndex <= 0) {
            return false;
        }

        // Validation for Review step - cannot go back if verification exists (must delete it first)
        if (currentStep === StepEnum.REVIEW && reviewVerification) {
            return false;
        }

        // Validation for Payment step - cannot go back if payment date exists (must clear it first)
        if (currentStep === StepEnum.PAYMENT && paymentMadeDate) {
            return false;
        }

        // Validation for Record step - cannot go back if record verification exists (must delete it first)
        if (currentStep === StepEnum.RECORD && recordVerification) {
            return false;
        }

        return true;
    };

    const handleStepChange = async (targetStep, direction = 'forward') => {
        if (direction === 'forward') {
            await handleStepComplete(targetStep);
        } else {
            setCurrentStep(targetStep);
        }
    };

    const goToNextStep = () => {
        if (!canGoToNextStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && !reviewVerification) {
                toast.error("Du måste skapa ett verifikat för momsrapporten innan du fortsätter");
            } else if (currentStep === StepEnum.RECORD && !recordVerification) {
                toast.error("Du måste skapa ett verifikat för dragningen innan du fortsätter");
            }
            return;
        }

        const nextStepIndex = getCurrentStepIndex() + 1;
        const nextStep = stepOrder[nextStepIndex];
        handleStepComplete(nextStep);
    };

    const goToPreviousStep = () => {
        if (!canGoToPreviousStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && reviewVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.PAYMENT && paymentMadeDate) {
                toast.error("Du måste ångra betalningsdatumet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.RECORD && recordVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            }
            return;
        }

        const previousStepIndex = getCurrentStepIndex() - 1;
        const previousStep = stepOrder[previousStepIndex];
        setCurrentStep(previousStep);
    };

    // Common props to pass to all step components
    const stepProps = {
        vatPeriod,
        currentStep,
        vatData,
        totalVat,
        transactions,
        totalDebit,
        totalCredit,
        companyId,
        vatPeriodId,
        fiscalYearId,
        accountNumbers,
        loading,
        error,
        verifying,
        undoingClose,
        handleStepComplete,
        handleVatPeriodClose,
        handleCompletePeriod,
        handleReviewVatPeriod,
        handlePayment,
        setShowModal,
        setShowVatModal,
        setTransactions,
        setTotalDebit,
        setTotalCredit,
        setError,
        setVerifying,
        goToNextStep,
        goToPreviousStep,
        canGoToNextStep,
        canGoToPreviousStep,
        calculateTotals,
        // Verification tracking props
        reviewVerification,
        recordVerification,
        paymentMadeDate,
        handleDeleteVerification,
        handlePaymentMade,
        handleClearPayment,
        handleCreateRecordVerification,
        operationInProgress,
        companyOrgNr: '556000-0175', // TODO: Get from company settings
        // Step completion tracking props
        markStepComplete,
        completionStatus
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{
                    height: '400px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    margin: '2rem 0'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: '3rem', height: '3rem' }}
                    >
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Laddar momsperiod...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="alert alert-danger"
                role="alert"
                style={{
                    borderRadius: '12px',
                    border: '1px solid #f5c6cb',
                    padding: '2rem',
                    margin: '2rem 0'
                }}
            >
                <h4 className="alert-heading">Ett fel uppstod</h4>
                <p className="mb-0">{error}</p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .vat-stepper-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 1rem;
                }
                
                .vat-content-wrapper {
                    background: #f8fafc;
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                
                /* Custom scrollbar for the container */
                .vat-stepper-container::-webkit-scrollbar {
                    width: 6px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                
                /* Mobile responsive adjustments */
                @media (max-width: 768px) {
                    .vat-stepper-container {
                        padding: 0 0.5rem;
                    }
                    
                    .vat-content-wrapper {
                        padding: 1rem 0;
                    }
                }
            `}</style>

            <div className="vat-content-wrapper">
                <div className="vat-stepper-container">
                    <VatModernHeader
                        vatPeriod={vatPeriod}
                        totalVat={totalVat}
                        undoingClose={undoingClose}
                        handleUndoClose={handleUndoClose}
                    />

                    <VatProgressTracker
                        currentStep={currentStep}
                        stepOrder={stepOrder}
                        stepTitles={stepTitles}
                        stepIcons={stepIcons}
                        isStepCompleted={isStepCompleted}
                        isStepActive={isStepActive}
                        onStepClick={null} // Disable click navigation for now
                    />

                    <VatStepContent
                        currentStep={currentStep}
                        stepProps={stepProps}
                    />

                    <VatStepNavigation
                        currentStep={currentStep}
                        stepOrder={stepOrder}
                        onStepChange={handleStepChange}
                        onCompletePeriod={handleCompletePeriod}
                        operationInProgress={operationInProgress}
                        canProceedToNext={canGoToNextStep()}
                        canGoBack={canGoToPreviousStep()}
                        vatPeriod={vatPeriod}
                        recordVerification={recordVerification}
                    />
                </div>
            </div>

            {/* Keep existing modals */}
            <VatVerificationModal
                showModal={showVatModal}
                setShowModal={setShowVatModal}
                transactions={transactions}
                totalDebit={totalDebit}
                totalCredit={totalCredit}
                verifying={verifying}
                error={error}
                handleConfirmVerification={verificationModalType === 'record' ? handleConfirmRecordVerification : handleConfirmVatVerification}
                vatPeriod={vatPeriod}
                modalType={verificationModalType}
            />

            <VatPaymentVerificationModal
                showModal={showModal}
                setShowModal={setShowModal}
                totalVat={totalVat}
                verifying={verifying}
                error={error}
                transactions={transactions}
                totalDebit={totalDebit}
                totalCredit={totalCredit}
                handleConfirmVerification={handleConfirmVerification}
            />
        </>
    );
}

export default VatPeriodStepper;